'use client';

import { useCallback, useState } from 'react';
import { BellRing } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RpgButton } from '@/features/lobby/ui/RpgButton';

/** localStorage gate. Bump the suffix when the copy materially changes
 *  so every returning subscriber re-acknowledges the new wording. */
const PUSH_BETA_NOTICE_KEY = 'takeover-push-beta-notice-v1';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function hasAcknowledgedPushBeta(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(PUSH_BETA_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

function markPushBetaAcknowledged() {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(PUSH_BETA_NOTICE_KEY, '1');
  } catch {
    // ignore quota / privacy-mode errors
  }
}

interface PushBetaNoticeModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * First-time push-notification disclaimer. Surfaced the moment a user
 * tries to enable push for the first time — *before* the browser's
 * permission prompt fires, so the user has explicitly acknowledged the
 * "best-effort delivery" framing before we kick off
 * `Notification.requestPermission()` (which on most browsers is a one-
 * shot affair: deny here and we can't ask again from JS, only from
 * site-settings).
 *
 * The acknowledgment is stored in localStorage under
 * {@link PUSH_BETA_NOTICE_KEY}; subsequent enable attempts skip the
 * dialog entirely until the key is cleared. The legal wording mirrors
 * Section 8 of the Terms of Use ("Notifications") so a user who reads
 * either surface gets the same message — point of failure, not a
 * substitute for monitoring positions onchain.
 */
export function PushBetaNoticeModal({
  open,
  onConfirm,
  onCancel,
}: PushBetaNoticeModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-lg bg-[#0b0a08] border-amber-500/30 text-zinc-100 shadow-[0_30px_120px_-40px_rgba(0,0,0,0.9)]">
        <DialogHeader className="border-b border-amber-500/20 pb-3">
          <DialogTitle className="text-amber-100 flex items-center gap-2">
            <BellRing className="h-4 w-4 text-amber-300" />
            Push notifications are in beta
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px] font-mono tracking-[0.2em] uppercase">
            Best-effort delivery
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-3 text-[12px] text-zinc-300 leading-relaxed">
          <p>
            Push notifications are in beta and should not be relied on
            fully. <strong className="text-amber-100">Always check the
            status of your land</strong> directly in the app — buyout
            alerts, expiry warnings, and chat mentions can be delayed,
            dropped, or fail to deliver without warning.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 sm:flex-row sm:justify-end">
          <RpgButton tone="silver" size="lg" silent onClick={onCancel}>
            Not now
          </RpgButton>
          <RpgButton tone="gold" size="lg" onClick={onConfirm}>
            Enable push
          </RpgButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Imperative gate around the push-subscribe flow. Returns:
 *
 *   • `gate(run)` — invoke when the user clicks "Enable push". If the
 *     viewer has already acknowledged the beta notice (via a previous
 *     accept), `run` fires immediately. Otherwise the modal opens and
 *     `run` is queued until the user accepts.
 *   • `modalProps` — spread onto `<PushBetaNoticeModal />` rendered by
 *     the same component. Cancelling the modal drops the queued run
 *     (the user backed out of enabling) without setting the localStorage
 *     flag, so a subsequent attempt re-prompts.
 *
 * Each consumer (the lobby `NotificationsDropdown` and the classic
 * `NotificationBell`) instantiates its own copy of the hook; the
 * acknowledgment flag is global so accepting in one surface skips the
 * modal everywhere on the same device.
 */
export function usePushBetaNoticeGate() {
  const [pendingRun, setPendingRun] = useState<(() => void | Promise<void>) | null>(null);

  const gate = useCallback((run: () => void | Promise<void>) => {
    if (hasAcknowledgedPushBeta()) {
      void run();
      return;
    }
    setPendingRun(() => run);
  }, []);

  const handleConfirm = useCallback(() => {
    markPushBetaAcknowledged();
    const run = pendingRun;
    setPendingRun(null);
    if (run) void run();
  }, [pendingRun]);

  const handleCancel = useCallback(() => {
    setPendingRun(null);
  }, []);

  return {
    gate,
    modalProps: {
      open: pendingRun !== null,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
}
